import { useQuery, type QueryKey } from '@tanstack/react-query';

import { DataTable, type DataTableColumn } from '../../../components/DataTable.tsx';
import { LoadingNotice } from '../../../components/AsyncState.tsx';
import { QueryFailureNotice } from '../QueryFailureNotice.tsx';

/**
 * The one query→table wiring shared by the five read-only flowsheet tabs (problems, allergies,
 * medications, immunizations, vitals) — `claude-kit/rules/invariants.md`'s "Favor the general,
 * reusable solution once a pattern actually recurs": fetch, render loading/`QueryFailureNotice`, or
 * hand rows to `DataTable` is the exact same shape five times over, so it is written once here
 * rather than five times inline in each tab file. Each tab file itself stays small: its own columns
 * and its own `queryFn`, nothing else.
 */
export function ChartTable<T>({
  queryKey,
  queryFn,
  columns,
  rowKey,
  minWidthRem,
  emptyMessage,
  loadingLabel,
  caption,
}: {
  readonly queryKey: QueryKey;
  readonly queryFn: (signal: AbortSignal) => Promise<readonly T[]>;
  readonly columns: readonly DataTableColumn<T>[];
  readonly rowKey: (row: T) => string;
  readonly minWidthRem?: number;
  readonly emptyMessage: string;
  readonly loadingLabel: string;
  readonly caption: string;
}) {
  const query = useQuery({ queryKey, queryFn: ({ signal }) => queryFn(signal) });

  if (query.isPending) return <LoadingNotice label={loadingLabel} />;
  if (query.isError) {
    return <QueryFailureNotice error={query.error} onRetry={() => void query.refetch()} />;
  }

  // Same `exactOptionalPropertyTypes` forwarding concern as `QueryFailureNotice.tsx` — `minWidthRem`
  // here is `number | undefined`, and `DataTable`'s own prop is `minWidthRem?: number`.
  return minWidthRem === undefined ? (
    <DataTable
      caption={caption}
      columns={columns}
      rows={query.data}
      rowKey={rowKey}
      emptyMessage={emptyMessage}
    />
  ) : (
    <DataTable
      caption={caption}
      columns={columns}
      rows={query.data}
      rowKey={rowKey}
      minWidthRem={minWidthRem}
      emptyMessage={emptyMessage}
    />
  );
}
